import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { ofType } from 'redux-observable';
import { EMPTY, of, concat } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';

import * as Actions from './scan-to-print.actions';
import * as Api from './scan-to-print.api';
import { STANDARD, LOCAL_DELIVERY } from '../../enums/csw-types';
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { getIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { SCAN_PRINTER_KEY, SCAN_MODE_KEY, scanOrderStatusDenyList } from '../../scan/constants';
import * as VerifyActions from '../../scan/verify/store/scan-to-verify.actions';
import { LocalStorageService } from '../../services/local-storage-service';
import { OrderSearchService } from '../../services/order-search.service';
import Global from '../../store/global-selectors';
import * as CSWActions from '../csw/csw.actions';
import { fulfillmentPlanToCSW } from '../csw/csw.fulfillment-plan-to-csw';
import * as JobPipelineActions from '../job-pipeline/job-pipeline.actions';
import { bulkLoadFulfillmentPlansFromScan$ } from '../orders/orders.observables';
import * as ProductActions from '../products/products.actions';
import * as PrintActions from '../scan-to-print/s2p-purchase-and-print.actions';
import { ScanToPrintReader } from '../scan-to-print/scan-to-print-reader';
import * as ShipmentDetailActions from '../shipments/shipment-details/shipments.details.actions';
import { ShipstationConnectReader } from '../shipstation-connect/shipstation-connect-reader';

const messages = defineMessages({
  orderNotFound: {
    description: 'scanToPrint.orderNotFound',
    defaultMessage: '{orderNumber} cannot be found',
    id: '3Qy48F',
  },
  shipmentNotFound: {
    description: 'scanToPrint.shipmentNotFound',
    defaultMessage: 'Shipment not found',
    id: '2VBK6k',
  },
  unshippableOrder: {
    description: 'scanToPrint cannot ship order error',
    defaultMessage: 'This order is in the {status} status and cannot be shipped. Scan a new packing slip to proceed.',
    id: 'FMzJsD',
  },
  disabledHotkeyNotification: {
    description: 'scanToPrint disabled hotkey notification',
    defaultMessage: 'Items must be verified in order to print label',
    id: 'M/2gjr',
  },
  queueComplete: {
    description: 'scanToPrint queue completion notification',
    defaultMessage: 'Scanning complete for {batchName}',
    id: 'RdKNtK',
  },
  orderLoadFailed: {
    description: 'scanToPrint queue order load failure',
    defaultMessage: 'Unable to load order {orderNumber}',
    id: 'lKoCbX',
  },
});

const getOrderNotFoundError = (searchValue) => ({
  ...messages.orderNotFound,
  values: { orderNumber: searchValue },
});

export const redirectToShipmentDetails = (action$) => {
  return action$.pipe(
    ofType(Actions.REDIRECT_TO_SHIPMENT_DETAILS),
    mergeMap(() => {
      const clear = of(Actions.clearOrderNumberSearch());
      return clear;
    }),
  );
};

export const setFulfillmentPlan = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SELECT_FULFILLMENT_PLAN),
    mergeMap(({ fulfillmentPlanId, orderGridStatus }) => {
      if (scanOrderStatusDenyList.includes(orderGridStatus)) {
        return concat(of(Actions.resetScanToPrintMode()), of(Actions.orderCannotBeShippedError(orderGridStatus)));
      }
      // go get the order
      return bulkLoadFulfillmentPlansFromScan$([fulfillmentPlanId]).pipe(
        mergeMap(() => {
          const state = getState();
          const reader = new ScanToPrintReader({ state });
          // get product records if in verify mode
          const orderStatus = Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'orderGridStatus' });
          if (scanOrderStatusDenyList.includes(orderStatus)) {
            return concat(of(Actions.resetScanToPrintMode()), of(Actions.orderCannotBeShippedError(orderStatus)));
          }
          if (reader.inVerifyMode) {
            const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId });
            const { items = [] } = fulfillmentPlan;
            const productIds = items.map((item) => item.productId).filter((x) => x);
            if (productIds.length) {
              return concat(
                of(Actions.clearOrderNumberSearch()),
                of(Actions.getShipmentSuccess(fulfillmentPlanId)),
                of(ProductActions.bulkLoadProductData(productIds)),
              );
            }
          }
          return concat(of(Actions.clearOrderNumberSearch()), of(Actions.getShipmentSuccess(fulfillmentPlanId)));
        }),
        catchError(() => of(Actions.setErrorMessage([getOrderNotFoundError(fulfillmentPlanId)]))),
      );
    }),
  );
};

const canScanToPrint = (state) => {
  const shipstationConnectReader = new ShipstationConnectReader({ state });
  const scanToPrintReader = new ScanToPrintReader({ state });
  return shipstationConnectReader.hasConnect || scanToPrintReader.inVerifyOnlyMode;
};

export const searchForFulfillmentPlan = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SEARCH_FOR_SCANNED_FULFILLMENT_PLAN),
    mergeMap((action) => {
      if (!canScanToPrint(getState())) {
        return EMPTY;
      }

      // TODO: look for fulfillmentPlan in entities before making backend request
      const { searchString } = action;
      const trimmed = OrderSearchService.getOrderId(searchString);
      const parsedFulfillmentPlanId = parseInt(trimmed, 16);
      const fulfillmentPlanId = parsedFulfillmentPlanId ? `${parsedFulfillmentPlanId}` : '';
      return concat(of(Actions.clearScanToErrors()), of(Actions.selectFulfillmentPlan(fulfillmentPlanId)));
    }),
  );
};

export const searchBoxSearch = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SEARCH_FOR_ORDER_NUMBER),
    mergeMap(({ searchValue }) => {
      if (!canScanToPrint(getState())) {
        return EMPTY;
      }

      if (OrderSearchService.isThisAnOrderId(searchValue)) {
        return concat(of(Actions.searchForScannedFulfillmentPlan(searchValue)), of(Actions.clearScanToErrors()));
      }

      const resetScanToPrintMode$ = of(Actions.resetScanToPrintMode());
      return Api.getOrdersForOrderNumber(searchValue).pipe(
        mergeMap((orderSearchResult) => {
          if (!orderSearchResult.hasOrders) {
            return concat(resetScanToPrintMode$, of(Actions.setErrorMessage([getOrderNotFoundError(searchValue)])));
          }

          if (orderSearchResult.hasOneFulfillmentPlan) {
            const { fulfillmentPlanId, orderGridStatus } = orderSearchResult.firstFulfillmentPlan;
            return concat(of(Actions.clearScanToErrors()), of(Actions.selectFulfillmentPlan(fulfillmentPlanId, orderGridStatus)));
          }
          const activeSalesOrders = orderSearchResult.salesOrders.filter((orders) => orders.orderStatus !== 'CAN');
          if (!activeSalesOrders.length) {
            return concat(
              of(Actions.resetScanToPrintMode()),
              of(Actions.orderCannotBeShippedError(FulfillmentPlanOrderGridStatus.Cancelled.name)),
            );
          }

          // Check if the search value is a tote barcode
          return Api.validateToteBarcode(searchValue).pipe(
            mergeMap((toteData) => {
              // Tote barcode is valid, queue all fulfillment plans
              const fulfillmentPlanIds = activeSalesOrders.flatMap((order) => order.fulfillmentPlans.map((fp) => fp.fulfillmentPlanId));
              return concat(of(Actions.clearScanToErrors()), of(Actions.initializeScanQueue(fulfillmentPlanIds, toteData.toteName)));
            }),
            catchError(() => {
              // Tote validation failed, fall back to showing order selection modal
              return concat(resetScanToPrintMode$, of(Actions.searchOrderNumberSuccess(activeSalesOrders)));
            }),
          );
        }),
        catchError(() => concat(resetScanToPrintMode$, of(Actions.setErrorMessage([getOrderNotFoundError(searchValue)])))),
      );
    }),
  );
};

export const printHotkey = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SCAN_TO_PRINT_PRINT_HOTKEY),
    mergeMap(({ isDisabled }) => {
      if (isDisabled) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.disabledHotkeyNotification} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return EMPTY;
      }
      const fulfillmentPlanId = Global.selectScanToPrintFulfillmentPlanId(getState());
      return fulfillmentPlanId ? of(PrintActions.startPurchaseAndPrintJob(fulfillmentPlanId)) : EMPTY;
    }),
  );
};

export const loadShipmentDetails = (action$) => {
  return action$.pipe(
    ofType(Actions.SCAN_TO_PRINT_LOAD_SHIPMENT_DETAILS),
    mergeMap(({ fulfillmentPlanId }) => {
      return fulfillmentPlanId ? of(ShipmentDetailActions.loadShipmentDetails(fulfillmentPlanId)) : EMPTY;
    }),
  );
};

export const closingShipmentDetails = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CLOSING_SCAN_TO_PRINT_SHIPMENT_DETAILS),
    mergeMap(() => {
      const state = getState();
      const scanToPrintJobId = Global.selectScanToPrintJobId(state);
      if (scanToPrintJobId) {
        return of(JobPipelineActions.exitJobPipeline(scanToPrintJobId));
      }

      const queue = Global.selectScanToPrintQueue(state);
      if (queue?.isActive) {
        // Don't reset when queue is active
        return of(Actions.advanceQueue());
      }

      return of(Actions.resetScanToPrintMode());
    }),
  );
};

export const shipmentDetailsLoadFailure = (action$, { getState }) => {
  return action$.pipe(
    ofType(ShipmentDetailActions.GET_SHIPMENT_DETAILS_FAILURE),
    mergeMap(({ shipmentId }) => {
      const fulfillmentPlanId = Global.selectScanToPrintFulfillmentPlanId(getState());
      if (fulfillmentPlanId !== shipmentId) {
        return EMPTY;
      }

      const errorMessage = messages.shipmentNotFound;

      return of(Actions.closingScanToPrintShipmentDetails(), Actions.setErrorMessage([errorMessage]));
    }),
  );
};

export const setupCsw = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SCAN_SETUP_CSW),
    mergeMap(({ fulfillmentPlanId }) => {
      const state = getState();
      const accountPackages = Global.selectSellerPackages(state) || [];
      const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
        fulfillmentPlanId,
      });
      if (!fulfillmentPlan.sellerLabelProviderId && fulfillmentPlan.serviceId) {
        const services = Global.selectSellerServices(state) || [];
        const selectedService = services.find((service) => service.serviceId === fulfillmentPlan.serviceId);
        const sellerLabelProviderId = selectedService?.sellerLabelProviderId;
        fulfillmentPlan.sellerLabelProviderId = sellerLabelProviderId;
      }
      const CSWShippingConfiguration = Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId });
      const updatePayload = fulfillmentPlanToCSW(
        fulfillmentPlan,
        accountPackages,
        CSWShippingConfiguration,
        Actions.SCAN_SETUP_CSW,
        getState(),
      );
      return of(
        CSWActions.setupShipmentConfiguration(fulfillmentPlanId, updatePayload, {
          type: getIsServiceLocalDelivery(state, updatePayload?.serviceId) ? LOCAL_DELIVERY : STANDARD,
        }),
      );
    }),
  );
};

export const popUpErrorToast = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_SCAN_TO_PRINT_ERRORS),
    mergeMap(({ errors = [] }) => {
      if (errors.forEach) {
        errors.forEach((err) => toast.warn(<FormattedMessage {...err} />));
      }
      return EMPTY;
    }),
  );
};

export const saveScanPrinterToLocalStorage = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_SCAN_TO_PRINT_PRINTER),
    mergeMap(({ printerId, workstationId }) => {
      LocalStorageService.setItem(SCAN_PRINTER_KEY, {
        printerId,
        workstationId,
      });
      return EMPTY;
    }),
  );
};

export const saveScanModeToLocalStorage = (action$) => {
  return action$.pipe(
    ofType(Actions.UPDATE_SCAN_TO_PRINT_MODE),
    mergeMap(({ mode }) => {
      LocalStorageService.setItem(SCAN_MODE_KEY, { mode });
      return EMPTY;
    }),
  );
};

export const orderCannotBeShippedError = (action$) => {
  return action$.pipe(
    ofType(Actions.ORDER_CANNOT_BE_SHIPPED_ERROR),
    mergeMap(({ orderGridStatus }) => {
      const statusMessage = FulfillmentPlanOrderGridStatus[orderGridStatus].translation;
      const status = <FormattedMessage {...statusMessage} />;
      toast.error(<FormattedMessage {...messages.unshippableOrder} values={{ status }} />, {
        autoClose: NOTIFICATION_DURATIONS.LONG,
        closeOnClick: true,
        progress: undefined,
      });
      return EMPTY;
    }),
  );
};

export const clearLeftoverToasts = (action$) => {
  return action$.pipe(
    ofType(Actions.CLEAR_SCAN_TO_ERRORS),
    mergeMap(() => {
      toast.dismiss();
      return EMPTY;
    }),
  );
};

export const initializeScanQueue = (action$) => {
  return action$.pipe(
    ofType(Actions.INITIALIZE_SCAN_QUEUE),
    mergeMap(({ fulfillmentPlanIds }) => {
      if (fulfillmentPlanIds.length > 0) {
        const firstFulfillmentPlanId = fulfillmentPlanIds[0];
        return of(Actions.selectFulfillmentPlan(firstFulfillmentPlanId));
      }
      return EMPTY;
    }),
  );
};

export const advanceScanQueue = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ADVANCE_QUEUE),
    mergeMap(() => {
      const state = getState();
      const queue = Global.selectScanToPrintQueue(state);

      if (!queue?.isActive) {
        return EMPTY;
      }

      if (queue.currentIndex >= queue.fulfillmentPlanIds.length) {
        NotificationService.showNotification({
          notificationType: 'success',
          content: <FormattedMessage {...messages.queueComplete} values={{ batchName: queue.batchName }} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
        return of(Actions.clearScanQueue(), Actions.resetScanToPrintMode());
      }

      const nextFulfillmentPlanId = queue.fulfillmentPlanIds[queue.currentIndex];
      return of(Actions.selectFulfillmentPlan(nextFulfillmentPlanId));
    }),
  );
};

export const autoAdvanceQueueOnWorkflowComplete = (action$, { getState }) => {
  return action$.pipe(
    ofType(PrintActions.LABEL_SUCCESSFULLY_CREATED_FOR_SCAN_TO_PRINT, VerifyActions.SET_VERIFICATION_STATUS_SUCCESS),
    mergeMap((action) => {
      const state = getState();
      const queue = Global.selectScanToPrintQueue(state);
      const reader = new ScanToPrintReader({ state });

      if (!queue?.isActive) {
        return EMPTY;
      }

      const shouldAdvance =
        action.type === PrintActions.LABEL_SUCCESSFULLY_CREATED_FOR_SCAN_TO_PRINT ||
        (action.type === VerifyActions.SET_VERIFICATION_STATUS_SUCCESS && reader.inVerifyOnlyMode);

      if (shouldAdvance) {
        return of(Actions.advanceQueue());
      }

      return EMPTY;
    }),
  );
};

export const handleQueueOrderLoadFailure = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SET_SCAN_TO_PRINT_ERRORS),
    mergeMap(({ errors = [] }) => {
      const state = getState();
      const queue = Global.selectScanToPrintQueue(state);

      if (!queue?.isActive || !errors.length) {
        return EMPTY;
      }

      const currentFulfillmentPlanId = queue.fulfillmentPlanIds[queue.currentIndex];
      const orderNotFoundError = errors.find((error) => error.id === messages.orderNotFound.id);

      if (orderNotFoundError) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.orderLoadFailed} values={{ orderNumber: currentFulfillmentPlanId }} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });

        return of(Actions.advanceQueue());
      }

      return EMPTY;
    }),
  );
};
